"use client";

import React, { useRef } from "react";

//Type
import {
  DictionaryProps,
  CardEmployeeProps,
  TableEmployeeProps,
} from "@/types";

//Style
import * as S from "./styles";

import ImageDefault from "@/assets/images/image-default.png";
import { getFileUrl } from "@/utils/getFileUrl";

//Icons
import {
  CalendarDays,
  Mail,
  Pencil,
  PhoneCall,
  Scissors,
  Star,
  Trash,
  User,
  Users,
} from "lucide-react";

//Components
import ButtonIcon from "../buttonIcon";
import Button from "../button";
import Start from "../start";
import Checkbox from "../form/checkbox";
import { toast } from "react-toastify";

// Função para gerar iniciais do nome
const getInitials = (name: string): string => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const CardService: React.FC<CardEmployeeProps> = ({
  className,
  data,
  dict,
  variant = "grid",
  handleUpdate,
  handleRemove,
  toggleSchedule,
  cantEdit,
  cantDelete,
  cantEditSchedule,
  handleUploadImage,
  onToggleShowInSchedule,
  onToggleCanViewAllSchedules,
}) => {
  // Ref para o input de arquivo
  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  const handleImageClick = (employeeId: string | number) => {
    if (handleUploadImage && fileInputRefs.current[employeeId]) {
      fileInputRefs.current[employeeId]?.click();
    }
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    employeeId: string | number,
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    // Validação de tamanho (máx 2MB)
    const MAX_SIZE = 2 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      toast.error("A imagem selecionada excede 2MB. Escolha um arquivo menor.");
      try {
        event.target.value = "";
      } catch {}
      return;
    }

    if (handleUploadImage) {
      handleUploadImage(employeeId, file);
    }
    // Limpar o input para permitir selecionar o mesmo arquivo novamente
    event.target.value = "";
  };

  return (
    <>
      {data.map((item: TableEmployeeProps, index: number) => {
        const agendaLabel =
          (dict && (dict["Agenda"] || dict["Schedule"])) || "Agenda";
        const viewAllLabel =
          dict?.["Can view all schedules"] ||
          dict?.["View all"] ||
          "Ver todas as agendas";

        // Grid variant
        if (variant === "grid") {
          return (
            <S.CardService className={className} key={item.id}>
              <S.TopTheBox>
                <S.HeaderCard>
                  <S.FirstBox>
                    <S.BoxImage>
                      {item.show_in_schedule_and_site ? (
                        <S.BoxAlertProduct $isLowStock={false}>
                          {agendaLabel}
                        </S.BoxAlertProduct>
                      ) : null}

                      {(() => {
                        let finalSrc: string | null = null;
                        const files = (item as any).files || [];
                        if (files.length > 0) finalSrc = getFileUrl(files[0]);
                        else if ((item as any).avatar_path)
                          finalSrc = getFileUrl({
                            path: (item as any).avatar_path,
                          });
                        else if ((item as any).image)
                          finalSrc = getFileUrl({ path: (item as any).image });

                        if (!finalSrc) {
                          const initials = getInitials(item.name || "");
                          return (
                            <S.AvatarPlaceholder
                              onClick={() => handleImageClick(item.id)}
                              className={
                                handleUploadImage ? "cursor-pointer" : ""
                              }
                            >
                              {initials}
                            </S.AvatarPlaceholder>
                          );
                        }

                        return (
                          <S.ImageEmployee
                            src={finalSrc}
                            onClick={() => handleImageClick(item.id)}
                            className={
                              handleUploadImage ? "cursor-pointer" : ""
                            }
                          />
                        );
                      })()}

                      <input
                        ref={(el) => {
                          if (el) fileInputRefs.current[item.id] = el;
                        }}
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) => handleFileChange(e, item.id)}
                      />
                    </S.BoxImage>
                    <S.TitleCard>{item.name}</S.TitleCard>
                    <S.TitleDescription>{item.profession}</S.TitleDescription>
                  </S.FirstBox>
                  <S.LastBox>
                    <Start score={item.score} />
                    <S.BoxButtons>
                      {cantEdit && (
                        <ButtonIcon
                          icon={<Pencil />}
                          hovercolor="green"
                          onClick={() => handleUpdate(item)}
                        />
                      )}
                      {item?.is_owner === 0 && cantDelete && (
                        <ButtonIcon
                          icon={<Trash />}
                          hovercolor="red"
                          onClick={() => handleRemove(Number(item.id))}
                        />
                      )}
                    </S.BoxButtons>
                  </S.LastBox>
                </S.HeaderCard>
                <S.BodyCard>
                  <S.ListItemEMployees>
                    {item.email && (
                      <S.ItemEmployee>
                        <Mail />
                        {item.email}
                      </S.ItemEmployee>
                    )}

                    {item.phone && (
                      <S.ItemEmployee>
                        <PhoneCall />
                        {item.phone}
                      </S.ItemEmployee>
                    )}

                    {item.clients && (
                      <S.ItemEmployee>
                        <Users />
                        {item.clients}
                      </S.ItemEmployee>
                    )}
                  </S.ListItemEMployees>

                  {cantEditSchedule && (
                    <S.FooterCard>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 8,
                        }}
                      >
                        <S.SmallControls style={{ alignItems: "flex-start" }}>
                          <Checkbox
                            htmlFor={`show_in_schedule_${item.id}`}
                            label={agendaLabel}
                            checked={!!item.show_in_schedule_and_site}
                            onChange={(e: any) =>
                              onToggleShowInSchedule &&
                              onToggleShowInSchedule(item.id, e.target.checked)
                            }
                          />

                          <Checkbox
                            htmlFor={`can_view_all_${item.id}`}
                            label={viewAllLabel}
                            checked={!!item.can_view_all_schedules}
                            onChange={(e: any) =>
                              onToggleCanViewAllSchedules &&
                              onToggleCanViewAllSchedules(
                                item.id,
                                e.target.checked,
                              )
                            }
                          />
                        </S.SmallControls>

                        <div>
                          <Button
                            text="Horários"
                            icon={<CalendarDays />}
                            variant="success"
                            onClick={() => toggleSchedule(item)}
                          />
                        </div>
                      </div>
                    </S.FooterCard>
                  )}
                </S.BodyCard>
              </S.TopTheBox>
            </S.CardService>
          );
        }

        // List variant
        return (
          <S.CardService className={className} key={item.id}>
            <S.Row>
              <S.RowLeft>
                <S.BoxImage>
                  {/* Badge */}
                  {item.show_in_schedule_and_site && (
                    <S.BoxAlertProduct $isLowStock={false}>
                      {agendaLabel}
                    </S.BoxAlertProduct>
                  )}

                  {(() => {
                    let finalSrc: string | null = null;
                    const files = (item as any).files || [];
                    if (files.length > 0) finalSrc = getFileUrl(files[0]);
                    else if ((item as any).avatar_path)
                      finalSrc = getFileUrl({
                        path: (item as any).avatar_path,
                      });
                    else if ((item as any).image)
                      finalSrc = getFileUrl({ path: (item as any).image });

                    if (!finalSrc) {
                      const initials = getInitials(item.name || "");
                      return (
                        <S.AvatarPlaceholder
                          onClick={() => handleImageClick(item.id)}
                          className={handleUploadImage ? "cursor-pointer" : ""}
                        >
                          {initials}
                        </S.AvatarPlaceholder>
                      );
                    }

                    return (
                      <S.ImageEmployee
                        src={finalSrc}
                        onClick={() => handleImageClick(item.id)}
                        className={handleUploadImage ? "cursor-pointer" : ""}
                      />
                    );
                  })()}

                  <input
                    ref={(el) => {
                      if (el) fileInputRefs.current[item.id] = el;
                    }}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => handleFileChange(e, item.id)}
                  />
                </S.BoxImage>

                <div>
                  <S.TitleCard>{item.name}</S.TitleCard>
                  <S.TitleDescription>{item.profession}</S.TitleDescription>
                </div>
              </S.RowLeft>

              <S.RowMiddle>
                {item.email && (
                  <S.ItemEmployee>
                    <Mail />
                    {item.email}
                  </S.ItemEmployee>
                )}
                {item.phone && (
                  <S.ItemEmployee>
                    <PhoneCall />
                    {item.phone}
                  </S.ItemEmployee>
                )}
                {item.clients && (
                  <S.ItemEmployee>
                    <Users />
                    {item.clients}
                  </S.ItemEmployee>
                )}
              </S.RowMiddle>

              <S.RowRight>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    alignItems: "flex-end",
                  }}
                >
                  <S.SmallControls>
                    <Checkbox
                      htmlFor={`show_in_schedule_${item.id}`}
                      label={agendaLabel}
                      checked={!!item.show_in_schedule_and_site}
                      onChange={(e: any) =>
                        onToggleShowInSchedule &&
                        onToggleShowInSchedule(item.id, e.target.checked)
                      }
                    />

                    <Checkbox
                      htmlFor={`can_view_all_${item.id}`}
                      label={viewAllLabel}
                      checked={!!item.can_view_all_schedules}
                      onChange={(e: any) =>
                        onToggleCanViewAllSchedules &&
                        onToggleCanViewAllSchedules(item.id, e.target.checked)
                      }
                    />
                  </S.SmallControls>

                  <div
                    style={{ display: "flex", gap: 8, alignItems: "center" }}
                  >
                    <Button
                      text="Horários"
                      icon={<CalendarDays />}
                      variant="success"
                      onClick={() => toggleSchedule(item)}
                    />
                    <S.BoxButtons>
                      {cantEdit && (
                        <ButtonIcon
                          icon={<Pencil />}
                          hovercolor="green"
                          onClick={() => handleUpdate(item)}
                        />
                      )}
                      {item?.is_owner === 0 && cantDelete && (
                        <ButtonIcon
                          icon={<Trash />}
                          hovercolor="red"
                          onClick={() => handleRemove(Number(item.id))}
                        />
                      )}
                    </S.BoxButtons>
                  </div>
                </div>
              </S.RowRight>
            </S.Row>
          </S.CardService>
        );
      })}
    </>
  );
};

export default CardService;
